import { createFileRoute, Link } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SeriesBadge } from "@/components/SeriesBadge";
import { SERIES_GROUPS, SERIES_PRODUCTS } from "@/data/series-products";
import {
  trackBadgeClick,
  trackBadgeHover,
  trackSeriesCta,
  trackSeriesProductClick,
} from "@/lib/analytics";
import { useBadgeGridImpression, useOnceFlag } from "@/hooks/use-badge-analytics";
import badgesBgPicture from "@/assets/pergola-blue-canopy-poolside-twilight.jpg?w=960;1440;1920&format=avif;webp;jpeg&as=picture";
const badgesBgSources = badgesBgPicture.sources as Record<string, string>;
const badgesBgFallback = badgesBgPicture.img.src as string;

type Row = { slug: string; path: string; name: string; tag: string; desc: string };

const RESIDENTIAL: Row[] = [
  { slug: "alamo-series", path: "/alamo-series", name: "Alamo", tag: "Residential", desc: "Residential Awnings & Shade" },
  { slug: "hill-country-series", path: "/hill-country-series", name: "Hill Country", tag: "Outdoor Living", desc: "Retractables, screens, curtains, sail shades, outdoor lighting, misting & heat" },
  { slug: "bluebonnet-series", path: "/bluebonnet-series", name: "Bluebonnet", tag: "Tents & Markets", desc: "Tents, Vendor Booths & Outdoor Markets" },
];
const COMMERCIAL: Row[] = [
  { slug: "longhorn-series", path: "/longhorn-series", name: "Longhorn", tag: "Commercial", desc: "Commercial Shade Systems" },
  { slug: "rio-grande-series", path: "/rio-grande-series", name: "Rio Grande", tag: "Hospitality & Resorts", desc: "Hotels, Resorts, Glamping, Safari Tents, Spas, Retreats & Event Venues" },
  { slug: "republic-series", path: "/republic-series", name: "Republic", tag: "Gov & Schools", desc: "Government & Schools" },
];
const AG_REC: Row[] = [
  { slug: "farming-ranching-series", path: "/farming-ranching-series", name: "Farming & Ranching", tag: "Agricultural", desc: "Ag Covers, Livestock Shade & Ranch Fabrication" },
  { slug: "outdoor-recreation-series", path: "/outdoor-recreation-series", name: "Outdoor Recreation", tag: "Hunt · Fish · Boat", desc: "Hunting, Fishing & Boating Covers" },
];
const SPECIALTY: Row[] = [
  { slug: "revival-series", path: "/revival-series", name: "Revival", tag: "Service & Recovery", desc: "Service & Recovery" },
  { slug: "lone-star-elite", path: "/lone-star-elite", name: "Lone Star Series", tag: "Smart Lighting & Automation", desc: "Bosso Smart Lighting, automated awnings, screens, sensors & app control" },
  { slug: "trail-boss", path: "/custom-fabrication", name: "Trail Boss", tag: "Welding & Fab", desc: "Welding & Fabrication" },
  { slug: "ranger-care", path: "/ranger-care", name: "Ranger Care", tag: "Membership", desc: "Maintenance Memberships" },
];

export const Route = createFileRoute("/series")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Texas Made Series — Choose Your Collection | Texas Made Shade",
      description:
        "Explore every Texas Made Shade series: Alamo, Hill Country, Bluebonnet, Longhorn, Rio Grande, Republic, Revival, Lone Star Series, Trail Boss, Ranger Care, Farming & Ranching, and Outdoor Recreation.",
      keywords:
        "texas made series, alamo series, hill country series, bluebonnet series, longhorn series, rio grande series, republic series, revival series, lone star elite, trail boss, ranger care, farming and ranching series, outdoor recreation series",
    }),
    links: leafCanonicalLink(ctx, "/series"),
  }),
  component: SeriesIndex,
});

const ALL_ROWS: Row[] = [...RESIDENTIAL, ...COMMERCIAL, ...AG_REC, ...SPECIALTY];

const GRID_PLACEMENT = "series_page_badges";
const CARD_PLACEMENT = "series_page_cards";

function SeriesIndex() {
  const badgeGridRef = useBadgeGridImpression<HTMLUListElement>(
    GRID_PLACEMENT,
    ALL_ROWS.map((r) => r.slug),
  );
  const firstTouch = useOnceFlag();

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <Breadcrumbs items={[{ label: "Series" }]} />
      <main id="main-content" tabIndex={-1}>
        <section className="relative overflow-hidden border-b-8 border-navy-deep bg-navy-deep py-14 text-white lg:py-20">
          <div className="absolute inset-0">
            <picture>
              {Object.entries(badgesBgSources).map(([type, srcSet]) => (
                <source key={type} type={`image/${type}`} srcSet={srcSet} sizes="100vw" />
              ))}
              <img
                src={badgesBgFallback}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover opacity-60"

                width={1920}
                height={1080}
                loading="lazy"
                decoding="async"
              />
            </picture>
            <div className="absolute inset-0 bg-navy-deep/20" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
            <div className="mb-10 text-center">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Choose Your Series ★</div>
              <h1 className="mt-3 font-display text-4xl font-black uppercase leading-tight text-white md:text-5xl">
                Choose Your Texas Made Series
              </h1>
              <p className="mx-auto mt-3 max-w-2xl text-base text-white/85 md:text-lg">
                Every project fits one of our signature series. Tap a badge to jump straight to the collection built for
                your home, business, project, and style.
              </p>
            </div>

            <ul ref={badgeGridRef} className="mx-auto grid max-w-6xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 sm:gap-4">
              {ALL_ROWS.map((r) => (
                <li key={r.slug + r.path} className="flex">
                  <Link
                    to={r.path}
                    className="group flex w-full flex-col items-center gap-2 rounded-lg border border-white/10 bg-navy-deep/70 p-3 text-center shadow-lg backdrop-blur-sm transition hover:-translate-y-1 hover:border-red/60 hover:bg-navy-deep/90"
                    aria-label={`${r.name} Series — ${r.desc}`}
                    onClick={() =>
                      trackBadgeClick({ slug: r.slug, name: r.name, placement: GRID_PLACEMENT }, r.path)
                    }
                    onMouseEnter={() => {
                      if (firstTouch(r.slug))
                        trackBadgeHover({ slug: r.slug, name: r.name, placement: GRID_PLACEMENT });
                    }}
                    onFocus={() => {
                      if (firstTouch(r.slug))
                        trackBadgeHover({ slug: r.slug, name: r.name, placement: GRID_PLACEMENT });
                    }}
                  >
                    <span className="flex h-24 w-full items-center justify-center md:h-28">
                      <SeriesBadge slug={r.slug} size="sm" className="max-h-full w-auto transition group-hover:scale-105" />
                    </span>
                    <span className="flex min-h-[3.25rem] flex-col justify-start">
                      <span className="font-display text-sm font-bold uppercase leading-tight text-white group-hover:text-red md:text-base">
                        {r.name}
                      </span>
                      <span className="mt-1 block font-display text-[10px] uppercase tracking-[0.15em] text-white/70">
                        {r.tag}
                      </span>
                      <span className="mt-1 block text-[11px] font-medium leading-tight text-white/80">{r.desc}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* WHAT'S UNDER EACH BADGE — market + full product list per series */}
        {SERIES_GROUPS.map((group) => {
          const entries = SERIES_PRODUCTS.filter((s) => s.group === group.key);
          if (entries.length === 0) return null;
          return (
            <section key={group.key} className="border-b border-border bg-background py-12 lg:py-16">
              <div className="mx-auto max-w-7xl px-4 lg:px-8">
                <h2 className="font-display text-2xl font-black uppercase leading-tight text-navy-deep md:text-3xl">
                  {group.title}
                </h2>
                <p className="mt-2 max-w-3xl text-sm text-muted-foreground md:text-base">{group.blurb}</p>

                <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {entries.map((s) => (
                    <article
                      key={s.slug}
                      className="flex flex-col rounded-xl border border-border bg-card p-5 shadow-sm transition hover:border-red/50 hover:shadow-md"
                    >
                      <div className="flex items-start gap-4">
                        <Link
                          to={s.path}
                          className="shrink-0"
                          aria-label={`${s.fullName} — ${s.market}`}
                          onClick={() =>
                            trackBadgeClick(
                              { slug: s.slug, name: s.name, placement: CARD_PLACEMENT },
                              s.path,
                            )
                          }
                        >
                          <SeriesBadge slug={s.slug} size="sm" className="w-16 md:w-20" />
                        </Link>
                        <div>
                          <h3 className="font-display text-lg font-bold uppercase leading-tight text-navy-deep">
                            <Link
                              to={s.path}
                              className="hover:text-red"
                              onClick={() =>
                                trackBadgeClick(
                                  { slug: s.slug, name: s.name, placement: CARD_PLACEMENT },
                                  s.path,
                                )
                              }
                            >
                              {s.fullName}
                            </Link>
                          </h3>
                          <p className="mt-1 font-display text-[11px] uppercase tracking-[0.15em] text-red">
                            {s.market}
                          </p>
                          <p className="mt-1 text-xs leading-snug text-muted-foreground">{s.audience}</p>
                        </div>
                      </div>

                      <p className="mt-5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-navy-deep/70">
                        Products under this badge
                      </p>
                      <ul className="mt-2 grid flex-1 gap-1.5">
                        {s.products.map((p) => (
                          <li key={`${s.slug}-${p.label}`} className="flex items-start gap-2 text-sm">
                            <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-red" />
                            <Link
                              to={p.to}
                              className="font-medium text-foreground hover:text-red hover:underline"
                              onClick={() =>
                                trackSeriesProductClick(
                                  { slug: s.slug, name: s.name, placement: CARD_PLACEMENT },
                                  p.label,
                                  p.to,
                                )
                              }
                            >
                              {p.label}
                            </Link>
                          </li>
                        ))}
                      </ul>

                      <Link
                        to={s.path}
                        onClick={() =>
                          trackSeriesCta(
                            { slug: s.slug, name: s.name, placement: CARD_PLACEMENT },
                            s.path,
                            `Explore ${s.name}`,
                          )
                        }
                        className="mt-5 inline-flex items-center justify-center rounded-md bg-navy-deep px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-white transition hover:bg-red"
                      >
                        Explore {s.name} →
                      </Link>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </main>
      <SiteFooter />
    </div>
  );
}
